import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const SERIF = "'PerfectNineties', serif";
const SANS  = "'MaisonNeueBook', sans-serif";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VaiaLogo = ({ height = 42 }) => (
  <img
    src="/vaia-icon.png"
    alt="VAIA"
    style={{ height, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
    onError={(e) => { e.target.src = '/logo.png'; }}
  />
);

// Landed here from AWS Marketplace fulfillment (see
// S:/Git/vaia-marketplace-entitlements). ?marketplace_token=... identifies
// the AWS customer whose subscription this account gets linked to. There's
// no other self-service signup page in this app — accounts are normally
// admin-provisioned — so this exists solely for the Marketplace flow.
export default function MarketplaceActivatePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('marketplace_token');
  const linkError = searchParams.get('marketplace_error');

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const inputStyle = {
    fontFamily: SANS,
    width: '100%',
    boxSizing: 'border-box',
    padding: '11px 14px',
    fontSize: 15,
    color: '#2d2f24',
    backgroundColor: '#fff',
    border: '1px solid #d4d0c8',
    outline: 'none',
    transition: 'border-color 0.15s',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, {
        full_name: fullName,
        email,
        password,
        marketplace_token: token,
      });
      toast.success('Account created. You can now sign in.');
      navigate('/login');
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Could not create your account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f6f1', fontFamily: SANS, display: 'flex', flexDirection: 'column' }}>
      <header style={{
        backgroundColor: '#fff', borderBottom: '1px solid rgba(0,0,0,0.07)',
        padding: '0 40px', height: 68, display: 'flex', alignItems: 'center',
      }}>
        <Link to="/" style={{ textDecoration: 'none' }}><VaiaLogo height={38} /></Link>
      </header>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 24px' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div style={{ backgroundColor: '#fff', border: '1px solid #e4e0d8', padding: '48px 44px 40px' }}>
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <VaiaLogo height={44} />
              </div>
              <h2 style={{ fontFamily: SERIF, fontSize: 28, color: '#2a2a2a', fontWeight: 400, margin: '0 0 8px' }}>
                Set Up Your Account
              </h2>
              <p style={{ fontFamily: SANS, fontSize: 14, color: '#888', margin: 0 }}>
                Finish setting up your VAIA Family Office subscription
              </p>
            </div>

            {(!token || linkError) ? (
              <p style={{ fontFamily: SANS, fontSize: 14, color: '#b23b3b', textAlign: 'center' }}>
                {linkError || 'This activation link is missing or invalid.'} Contact foundersoffice@vaia.co.in for help.
              </p>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontFamily: SANS, fontSize: 13, color: '#555', display: 'block', marginBottom: 6 }}>Full Name</label>
                  <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required style={inputStyle} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontFamily: SANS, fontSize: 13, color: '#555', display: 'block', marginBottom: 6 }}>Email Address</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required style={inputStyle} />
                </div>
                <div style={{ marginBottom: 28 }}>
                  <label style={{ fontFamily: SANS, fontSize: 13, color: '#555', display: 'block', marginBottom: 6 }}>Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      minLength={8}
                      required
                      style={{ ...inputStyle, paddingRight: 42 }}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', padding: 0 }}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    fontFamily: SANS, width: '100%', padding: '12px',
                    backgroundColor: loading ? '#ccc' : '#ffd879',
                    border: '0.5px solid #2a2a2a', borderRadius: 50, fontSize: 16,
                    color: '#2d2f24', cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? 'Creating account…' : 'Create Account'}
                </button>
              </form>
            )}
          </div>
          <p style={{ fontFamily: SANS, fontSize: 12, color: '#bbb', textAlign: 'center', marginTop: 24 }}>
            VAIA Investment Advisory LLP © 2026 · Secure & Private
          </p>
        </div>
      </main>
    </div>
  );
}
